import React from 'react';
import { omit, orderBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useMutation, useQuery } from '@apollo/client';
import { client } from '~/apollo';
import {
    ListDocumentsDocument,
    DokumentInput,
    Dokument,
    EinsatzberichtDetail,
    CreateDocumentDocument,
    DeleteDocumentDocument,
    UpdateOperationReportDetailDocument,
    ListOperationReportDetailsDocument,
    ListCorrespondencesAndEventsDocument,
    UpdateDocumentDocument,
    KorrespondenzCreateInput,
    CreateCorrespondenceAndDocumentsDocument,
    KorrespondenzDokument,
} from '~/gql/ucpw/graphql';
import { tables } from '~/pages/projects/meta/data/documents.schema';
import { hasLockedCorrespondenceReference } from '~/pages/projects/meta/data/lockedCorrespondence';
import { ProjectIds } from '~/pages/projects/types';
import { Modals } from '~/pages/projects/ui/Documents';
import { parseJsonRecursively, withRenderers } from '~/utils';
import {
    useContextAndRefetchQueries,
    useDeleteWithConfirmation,
    useModal,
    useAsset,
    usePermission,
    useSnippetId,
    useViewer,
} from '~/hooks';
import log from '~/log';

type Props = {
    tableName?: 'documents';
    refetchQueries?: [];
} & ProjectIds;

export const useDocuments = ({
    tableName = 'documents',
    subprojectId,
    projectId,
    ...props
}: Props) => {
    const viewer = useViewer();
    const navigate = useNavigate();
    const mitarbeiterIdSachbearbeiter = viewer?.app?.mitarbeiterId;
    const { typSnippetId } = useSnippetId({
        name: 'Sonstiges',
        category: 'Korrespondenz',
        property: 'Typ',
    });
    const { canEdit, canCreate, canDelete, canView } = usePermission('project.documents');
    const readOnly = canView && !(canCreate || canEdit);

    const { canEdit: canEditCorrespondence, canDelete: canDeleteCorrespondence } =
        usePermission('project.correspondence');
    const { dispatch, onOpen } = useModal();
    const { upload } = useAsset();

    const context = { clientName: 'ucpw' };
    const pageSize = 100;
    const variables = { filter: { subprojektId: subprojectId }, limit: pageSize };
    const contextAndRefetchQueries = useContextAndRefetchQueries(ListDocumentsDocument, variables);

    // ===============================================
    // LIST
    // ===============================================

    const {
        data: listData,
        loading: listLoading,
        error: listError,
    } = useQuery(ListDocumentsDocument, { variables, context });

    const documents = orderBy(listData?.items?.items || [], ['id'], ['desc']);

    const refetchQueries = [
        // internal
        ...contextAndRefetchQueries.refetchQueries,
        // external
        ...(props.refetchQueries || []),
        {
            query: ListCorrespondencesAndEventsDocument,
            variables: { subprojektId: subprojectId },
            context,
        },
    ];

    // ===============================================
    // CREATE
    // ===============================================

    const options = { context, refetchQueries };
    const [createMutation] = useMutation(CreateDocumentDocument, options);
    const [createCorrespondenceAndDocumentsMutation] = useMutation(
        CreateCorrespondenceAndDocumentsDocument,
        options
    );

    const generateFileName = (fileName: string = '') => {
        const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
        if (!Boolean(fileName)) {
            return `dokument_${timestamp}.pdf`;
        }

        if (fileName.includes('.pdf')) {
            const [name] = fileName.split('.');
            return [name, timestamp].join('_') + '.pdf';
        }

        return [fileName, timestamp].join('_') + '.pdf';
    };

    const createDocumentFromSignedUrl = React.useCallback(
        async (
            signedUrl: string,
            {
                data,
                fileName,
                freitext,
            }: { data: DokumentInput; fileName?: string; freitext?: string }
        ) => {
            const documentName = generateFileName(fileName);

            try {
                const oReq = new XMLHttpRequest();
                oReq.open('GET', signedUrl, true);
                oReq.responseType = 'blob';
                oReq.onload = async function () {
                    const file = new File(
                        [oReq.response],
                        data?.name ? generateFileName(data?.name) : documentName,
                        { type: 'application/pdf' }
                    );
                    const { key, name }: any = await upload(file);
                    const documents = [
                        { name, pfad: key, uploadDatum: new Date(), ...omit(data, 'name', 'pfad') },
                    ];
                    const correspondence: KorrespondenzCreateInput = {
                        // subprojektId: Int!;
                        subprojektId: subprojectId as number,
                        // typSnippetId: Int!;
                        typSnippetId,
                        // freitext: AWSJSON!;
                        freitext,
                        // mitarbeiterIdSachbearbeiter: Int;
                        mitarbeiterIdSachbearbeiter,
                        // mitarbeiterIdUrheber: Int;
                        mitarbeiterIdUrheber: mitarbeiterIdSachbearbeiter,
                        // zeit: AWSDateTime;
                        zeit: new Date(),
                        // beteiligter: String!;
                        beteiligter: ' ',
                    };

                    if ([subprojectId, typSnippetId, freitext].every(Boolean)) {
                        const response = await createCorrespondenceAndDocumentsMutation({
                            variables: {
                                correspondence,
                                documents,
                            },
                        });
                        log.debug('createDocumentFromSignedUrl', {
                            payload: { correspondence, documents },
                            response,
                        });
                    }
                };
                oReq.send();
            } catch (err) {
                console.log(err);
            }
        },
        [typSnippetId, mitarbeiterIdSachbearbeiter, createCorrespondenceAndDocumentsMutation]
    );

    // ===============================================
    // UPDATE
    // ===============================================

    const [updateMutation, { loading: updateLoading }] = useMutation(
        UpdateDocumentDocument,
        options
    );

    // ===============================================
    // UPDATE OPERATIONAL DETAIL
    // ===============================================

    const [updateOperationalDetailMutation, { loading: updateOperationalDetailLoading }] =
        useMutation(UpdateOperationReportDetailDocument, options);

    const upsertOperationalDetails = async (
        document: Dokument,
        mutationType: 'update' | 'delete' = 'update'
    ) => {
        const response = await client.query({
            query: ListOperationReportDetailsDocument,
            context,
            fetchPolicy: 'network-only',
            variables: {
                filter: { einsatzberichtId: document?.einsatzberichtId },
                einsatzberichtOption: { bezeichnungPrefix: 'fotodokumentation' },
            },
        } as any);
        const details = response?.data?.items?.items || [];
        const data = details?.find(
            (detail: EinsatzberichtDetail) =>
                detail?.einsatzberichtOption?.bezeichnung === 'fotodokumentation'
        );

        const parsedCollection = parseJsonRecursively(data?.text);
        const photoCollection = Array.isArray(parsedCollection) ? parsedCollection : [];

        const upsertOperationalDetail =
            (original: Dokument) => (type: 'update' | 'delete', data: any) => {
                if (data?.attachment?.key === original?.pfad) {
                    return type === 'update'
                        ? {
                              id: data?.id,
                              description: original?.beschreibung,
                              attachment: {
                                  key: original?.pfad,
                                  fileName: original?.name,
                              },
                              fileType: {
                                  value: original?.typSnippet?.id,
                                  label: original?.typSnippet?.name,
                                  key: original?.typSnippet?.kuerzel,
                              },
                          }
                        : null;
                }
                return data;
            };

        const upsert = upsertOperationalDetail(document);
        const variables: any = {
            id: data?.id,
            data: {
                einsatzberichtId: document?.einsatzberichtId,
                einsatzberichtOptionId: data?.einsatzberichtOption?.id,
                text: JSON.stringify(
                    photoCollection
                        ?.map?.((data: any) => upsert(mutationType, data))
                        .filter(Boolean)
                ),
            },
        };

        return async () => await updateOperationalDetailMutation({ variables });
    };

    // ===============================================
    // DELETE
    // ===============================================

    const [deleteMutation, { loading: deleteLoading }] = useMutation(
        DeleteDocumentDocument,
        options
    );
    const { deleteWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation,
        refetchQueries,
        dependencies: [deleteMutation, refetchQueries],
    });

    // ===============================================
    // TABLE
    // ===============================================

    const onEdit = (row: any) => {
        log.debug('useDocuments.onEdit', row.original);
        dispatch?.({
            type: 'setModal',
            data: {
                modal: Modals.Documents,
                props: {
                    createMutation,
                    updateMutation,
                    deleteMutation,
                    subprojectId,
                },
            },
        });
        dispatch?.({
            type: 'setRow',
            data: {
                row,
                upsertOperationalDetails,
            },
        });
        onOpen?.();
    };

    const onMove = (row: any) => {
        log.debug('useDocuments.onMove', row.original);
        dispatch?.({
            type: 'setModal',
            data: { modal: Modals.ProjectSearch },
        });
        dispatch?.({
            type: 'formState',
            data: {
                ...row?.original,
                updateMutation,
            },
        });
        onOpen?.();
    };

    const onDelete = async (row: any) => {
        const upsert = await upsertOperationalDetails(row?.original, 'delete');
        const options = row?.original?.einsatzberichtId
            ? { callAfter: async () => await upsert() }
            : {};

        deleteWithConfirmation({ id: row?.original?.id }, options);
    };

    const enable =
        (
            title:
                | 'Ansehen'
                | 'Bearbeiten'
                | 'Verschieben'
                | 'Löschen'
                | 'zur Korrespondenz'
                | 'divider'
        ) =>
        ({ original }: { original: Dokument }) => {
            const correspondenceDocument = (original?.korrespondenzDokument ||
                []) as KorrespondenzDokument[];
            const hasCorrespondenceDocument = correspondenceDocument?.length > 0;

            if (hasCorrespondenceDocument) {
                // Anhang einer versendeten Schnittstellen-Nachricht (GDV / PX Connect):
                // ansehen ja, alles andere nein — wie die Nachricht selbst.
                const isLocked = hasLockedCorrespondenceReference(correspondenceDocument);

                if (isLocked) {
                    switch (title) {
                        case 'Ansehen':
                        case 'zur Korrespondenz':
                            return true;
                        default:
                            return false;
                    }
                } else {
                    const correspondenceCreatorMatchesViewer = correspondenceDocument?.find(
                        (corrDoc: KorrespondenzDokument) => {
                            const creatorId = corrDoc?.korrespondenz?.mitarbeiterIdUrheber;
                            return creatorId === mitarbeiterIdSachbearbeiter || creatorId === null;
                        }
                    );
                    switch (title) {
                        case 'Ansehen':
                            return (
                                readOnly ||
                                isLocked ||
                                correspondenceCreatorMatchesViewer === undefined
                            );
                        case 'Bearbeiten':
                            return (
                                canEdit &&
                                canEditCorrespondence &&
                                Boolean(correspondenceCreatorMatchesViewer)
                            );
                        case 'zur Korrespondenz':
                            return true;
                        case 'Verschieben':
                        case 'divider':
                        case 'Löschen':
                        default:
                            return false;
                    }
                }
            } else {
                switch (title) {
                    case 'Ansehen':
                        return readOnly;
                    case 'Bearbeiten':
                        return canEdit;
                    case 'Verschieben':
                        return canEdit;
                    case 'divider':
                    case 'Löschen':
                        return canDelete;
                    case 'zur Korrespondenz':
                    default:
                        return false;
                }
            }
        };

    /** don't touch it! Matches permissions requirements of UCPWO-29 regarding uploaded correspondence documents */
    const controls = [
        {
            title: 'Ansehen',
            props: { onClick: onEdit },
            enabled: enable('Ansehen'),
        },
        {
            title: 'Bearbeiten',
            props: { onClick: onEdit },
            enabled: enable('Bearbeiten'),
        },
        {
            title: 'Verschieben',
            props: { onClick: onMove },
            enabled: enable('Verschieben'),
        },
        {
            title: 'zur Korrespondenz',
            props: {
                onClick: (row: any) => {
                    const state = row?.original || {};
                    navigate(`/projekte/${projectId}/${subprojectId}/details`, { state });
                },
            },
            enabled: enable('zur Korrespondenz'),
        },
        {
            title: 'divider',
            enabled: enable('divider'),
        },

        {
            title: 'Löschen',
            props: { color: 'red.400', onClick: onDelete },
            enabled: enable('Löschen'),
        },
    ].filter(Boolean);

    const onCreate = () => {
        dispatch?.({
            type: 'setModal',
            data: {
                modal: Modals.Documents,
                props: {
                    createMutation,
                    updateMutation,
                    deleteMutation,
                    subprojectId,
                },
            },
        });
        dispatch?.({
            type: 'setRow',
            data: {
                row: { original: {} },
                upsertOperationalDetails,
            },
        });
        onOpen?.();
    };

    const columns = React.useMemo(() => withRenderers(tables[tableName].columns, controls), []);
    const hiddenColumns = tables[tableName].hiddenColumns;

    const loading = listLoading || deleteLoading || updateOperationalDetailLoading || updateLoading;
    const error = listError;

    return {
        data: documents,
        pageSize,
        loading,
        error,
        columns,
        hiddenColumns,
        createMutation,
        createDocumentFromSignedUrl,
        updateMutation,
        onCreate,
    };
};
